import { useEffect, useState } from 'react';
import { makeStyles, Skeleton, SkeletonItem } from "@fluentui/react-components";
import blogService from '../../services/BlogService';
import Hero from '../../components/hero/Hero';
import { useTranslation } from 'react-i18next';
import Categories from './Categories';
import FeaturedBlogCard from './FeaturedBlogCard';
import BlogCard from './BlogCard';
import Status from '../../enums/Status';
import IEntryPreivew from '../../models/blog/IEntryPreview';
import Pagination from './Pagination';

const BLOGS_PER_PAGE = 9;
const BLOGS_ON_FIRST_PAGE = 10;

const useStyles = makeStyles({
	mainContainer: {
		padding: 0,
		backgroundColor: '#f5f5f5',
	},
	blogs: {
		gap: "16px",
		display: "flex",
		flexWrap: "wrap",
		justifyContent: "start",
		width: "calc(100% - 2rem)",
		maxWidth: "1200px",
		margin: "1rem auto",
		padding: "0 1rem",
	},
	skeleton: {
		width: 'calc(33% - 8px)',
        margin: '0',

		"&:first-of-type": {
			width: '100%'
		},

        "@media (max-width: 768px)": {
            width: '100%'
        }
	},
	skeletonItem: {
		width: "100%",
		height: "300px",
	},
});

const Blogs = () => {
	const { t } = useTranslation("blog");
	const styles = useStyles();

	const [status, setStatus] = useState<Status>(Status.LOADING);
	const [page, setPage] = useState<number>(1);
	const [blogs, setBlogs] = useState<IEntryPreivew[]>([]);
	const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

	useEffect(() => {
    	fetchBlogs(setBlogs, setStatus);
  	}, []);

	const onSelectedCategoriesChange = (categories: string[]) => {
		setSelectedCategories(categories);
		setPage(1);
	};
		

	const filteredBlogs = selectedCategories.length === 0
		? blogs
		: blogs.filter(b => b.tags.some(t => selectedCategories.includes(t)));
	
	const pages =
		selectedCategories.length === 0
			? (blogs.length <= BLOGS_ON_FIRST_PAGE
				? 1
				: 1 + Math.ceil((blogs.length - BLOGS_ON_FIRST_PAGE) / BLOGS_PER_PAGE))
			: Math.max(1, Math.ceil(filteredBlogs.length / BLOGS_PER_PAGE));

  	return (
		<div className={styles.mainContainer}>
			<Hero
				title={t('hero.title')}
				subtitle={t('hero.description')}
				imageUri="/img/banners/blogs.jpg"
				/>

			<Categories
				selectedCategories={selectedCategories}
				setSelectedCategories={onSelectedCategoriesChange}
				/>

			{
				status === Status.LOAD_SUCCESS &&
				blogs.length > 0 &&
				<Pagination
					pages={pages}
					selectedPage={page}
					setSelectedPage={setPage}
					/>
			}
			
			{
				<div className={styles.blogs}>
					{
						status === Status.LOADING &&
						Array.from({ length: 10 }).map((_, i) => (
							<Skeleton key={i} className={styles.skeleton}>
								<SkeletonItem className={styles.skeletonItem} shape="rectangle" />
							</Skeleton>
						))
					}
					{
						status === Status.LOAD_SUCCESS &&
						blogs.length > 0 &&
						page === 1 &&
						selectedCategories.length === 0
							? 	<>
									<FeaturedBlogCard blog={blogs[0]} />
									{
										blogs
											.slice(1, Math.min(BLOGS_ON_FIRST_PAGE, blogs.length))
											.map(b => <BlogCard key={b.id} blog={b} />)
									}
								</>
							:	<>
									{
										(
											selectedCategories.length === 0
												? blogs
												: blogs.filter(b => b.tags.some(t => selectedCategories.includes(t)))
										)
											.slice(
												selectedCategories.length === 0
													? (page === 1
														? BLOGS_ON_FIRST_PAGE
														: BLOGS_ON_FIRST_PAGE + (page - 2) * BLOGS_PER_PAGE)
													: (page - 1) * BLOGS_PER_PAGE,
												selectedCategories.length === 0
													? (page === 1
														? BLOGS_ON_FIRST_PAGE + BLOGS_PER_PAGE
														: BLOGS_ON_FIRST_PAGE + (page - 1) * BLOGS_PER_PAGE)
													: page * BLOGS_PER_PAGE
											)
											.map(b => <BlogCard key={b.id} blog={b} />)
									}
								</>
					}
				</div>
			}
		</div >
	);
};

export default Blogs;

async function fetchBlogs(
	setBlogs: (blogs: IEntryPreivew[]) => void,
	setStatus: (status: Status) => void
) {
	setStatus(Status.LOADING);

	try {
		const blogs = await blogService.getBlogPreviews();

		setBlogs(blogs);
		setStatus(Status.LOAD_SUCCESS);
	} catch (_) {
		setStatus(Status.LOAD_ERROR);
	}
}